(() => {
  const root = document.querySelector('[data-advent-brew-guides]');
  const sliderElement = root?.querySelector('[data-advent-slider]');

  if (!root || !sliderElement || sliderElement.swiper || typeof Swiper === 'undefined') return;

  function ignoreCancellation(error) {
    if (error.name !== 'AbortError') console.error('Advent animation:', error);
  }

  function createSequence() {
    let controller;
    return {
      cancel() {
        controller?.abort();
      },
      run(task) {
        controller?.abort();
        controller = new AbortController();
        const signal = controller.signal;
        return Promise.resolve()
          .then(() => {
            signal.throwIfAborted();
            return task(signal);
          })
          .catch(ignoreCancellation);
      }
    };
  }

  function wait(duration, signal) {
    signal?.throwIfAborted();
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => {
        cleanup();
        resolve();
      }, duration);
      const abort = () => {
        window.clearTimeout(timer);
        cleanup();
        reject(signal.reason);
      };
      const cleanup = () => signal?.removeEventListener('abort', abort);
      signal?.addEventListener('abort', abort, { once: true });
    });
  }

  async function animate(element, frames, options, signal) {
    signal?.throwIfAborted();
    const animation = element.animate(frames, { fill: 'both', ...options });
    const abort = () => animation.cancel();
    signal?.addEventListener('abort', abort, { once: true });
    try {
      await animation.finished;
    } finally {
      signal?.removeEventListener('abort', abort);
      animation.cancel();
    }
    signal?.throwIfAborted();
  }

  // One cached decode per sheet, even though the two character actors share it.
  const sheetLoads = new Map();
  function loadSheet(src) {
    if (!sheetLoads.has(src)) {
      const image = new Image();
      image.src = src;
      const timeout = new AbortController();
      const loaded = Promise.race([
        image.decode().catch(() => {}),
        wait(2000, timeout.signal)
      ]).finally(() => timeout.abort());
      sheetLoads.set(src, loaded);
    }
    return sheetLoads.get(src);
  }

  function createSprite(viewport, sheet, reducedMotion) {
    const stride = sheet.width + sheet.gutter * 2;
    const sheetWidth = stride * sheet.columns;
    const image = document.createElement('img');
    image.alt = '';
    image.draggable = false;
    image.width = sheetWidth;
    image.height = sheet.height;
    viewport.style.setProperty('--sprite-sheet-width', `${(sheetWidth / sheet.width) * 100}%`);
    viewport.style.setProperty('--sprite-aspect', `${sheet.width} / ${sheet.height}`);
    viewport.append(image);
    let animation;
    let current = viewport.dataset.pose;
    // Keep neighboring artwork beyond the clip edge, even at fractional screen pixels.
    const transform = (name) =>
      `translateX(${(-(sheet.frames[name].index * stride + sheet.gutter) * 100) / sheetWidth}%)`;

    function load() {
      if (!image.hasAttribute('src')) image.src = sheet.src;
      return loadSheet(sheet.src);
    }
    function stop() {
      animation?.cancel();
      animation = undefined;
    }
    function show(name) {
      stop();
      current = String(name);
      viewport.style.transform = sheet.frames[current].mirror ? 'scaleX(-1)' : '';
      image.style.transform = transform(current);
    }
    async function play(
      frames,
      { duration, frameDuration = duration / frames.length, iterations = 1, signal } = {}
    ) {
      signal?.throwIfAborted();
      show(frames[0]);
      // Start loading once; frame playback itself never replaces image.src.
      load();
      if (reducedMotion) return;
      const count = Math.ceil(duration / frameDuration);
      const last = frames[(count - 1) % frames.length];
      const keyframes = Array.from({ length: count }, (_, index) => ({
        transform: transform(frames[index % frames.length]),
        offset: (index * frameDuration) / duration,
        easing: 'steps(1, end)'
      }));
      keyframes.push({ transform: transform(last), offset: 1 });
      const active = image.animate(keyframes, { duration, iterations, fill: 'both' });
      animation = active;
      const abort = () => active.cancel();
      signal?.addEventListener('abort', abort, { once: true });
      try {
        await active.finished;
        current = String(last);
        image.style.transform = transform(current);
      } finally {
        signal?.removeEventListener('abort', abort);
        active.cancel();
        if (animation === active) animation = undefined;
      }
      signal?.throwIfAborted();
    }
    show(current);
    return { load, show, play, stop };
  }

  function createParallax(root, reducedMotion) {
    const definitions = [
      ['.advent-wall', 494 / 288, 0.75, true],
      ['.advent-plants', 304 / 141, 0.5, true],
      ['.advent-floor', 1, 1, false]
    ];
    const layers = definitions.map(([selector, ratio, speed, centered]) => ({
      clip: root.querySelector(selector),
      strip: root.querySelector(`${selector} .advent-scenery`),
      ratio,
      speed,
      centered,
      tile: 1
    }));
    let frame;
    let instance;
    let needsMeasure = true;
    let visible = true;

    function render() {
      frame = undefined;
      if (!instance || instance.destroyed || !visible || document.hidden) return;
      if (needsMeasure) {
        // Measure only on resize. Each strip extends one tile past both clipped edges.
        const sizes = layers.map(({ clip, ratio, centered }) => {
          const bounds = clip.getBoundingClientRect();
          return {
            width: bounds.width,
            tile: centered
              ? bounds.height * ratio
              : Number.parseFloat(getComputedStyle(clip, '::before').width)
          };
        });
        layers.forEach((layer, i) => {
          layer.tile = Math.max(1, sizes[i].tile);
          layer.strip.style.width = `${sizes[i].width + layer.tile * 2}px`;
          layer.strip.style.left = `${-layer.tile}px`;
          layer.strip.style.backgroundSize = `${layer.tile}px auto`;
        });
        needsMeasure = false;
      }
      const distance = reducedMotion ? 0 : instance.getTranslate() - instance.minTranslate();
      layers.forEach(({ strip, tile, speed }) => {
        const offset = (((distance * speed) % tile) + tile) % tile;
        strip.style.transform = `translate3d(${offset}px, 0, 0)`;
      });
      if (instance.animating && !reducedMotion) frame = requestAnimationFrame(render);
    }
    function update(swiper, measure = false) {
      instance = swiper;
      needsMeasure ||= measure;
      if (frame === undefined && visible && !document.hidden) frame = requestAnimationFrame(render);
    }
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) update(instance);
      else {
        cancelAnimationFrame(frame);
        frame = undefined;
      }
    });
    observer.observe(root);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        cancelAnimationFrame(frame);
        frame = undefined;
      } else update(instance);
    });
    return { update };
  }

  function createGuideController(root, reducedMotion, canOpen, onChange) {
    const modal = document.querySelector('[data-advent-modal]');
    const modalOverlay = document.querySelector('[data-advent-modal-overlay]');
    const modalCloseButton = modal?.querySelector('[data-advent-modal-close]');
    const modalDialog = modal?.querySelector('.advent-guide-dialog');
    const guideContent = modal?.querySelector('[data-advent-guide-content]');
    const batchTemplate = modal?.querySelector('[data-advent-batch-guide]');
    const guideTemplates = new Map(
      Array.from(document.querySelectorAll('[data-advent-guide]'), (template) => [
        Number(template.dataset.adventIndex),
        template
      ])
    );
    const videoFrame = modal?.querySelector('[data-advent-video-frame]');
    const videoMount = modal?.querySelector('[data-advent-video-mount]');
    const videoStatus = modal?.querySelector('[data-advent-video-status]');
    const videoMessage = modal?.querySelector('[data-advent-video-message]');
    const videoRetry = modal?.querySelector('[data-advent-video-retry]');
    let modalCloseTimeout;
    let modalReturnFocus;
    let modalIsOpen = false;
    let openGuideIndex;
    let wistiaScriptPromise;
    let activeVideo;
    let activeVideoSlot;
    let videoRequest = 0;
    let videoLoadTimeout;
    function loadWistia() {
      if (window.Wistia && typeof window.Wistia.embed === 'function') return Promise.resolve();
      if (wistiaScriptPromise) return wistiaScriptPromise;

      // Share the reference modal's E-v1 runtime, but request it only for an opened video.
      wistiaScriptPromise = new Promise((resolve, reject) => {
        const existingScript = document.querySelector(
          'script[src*="wistia.com/assets/external/E-v1.js"], script[src*="wistia.net/assets/external/E-v1.js"]'
        );
        const script = existingScript || document.createElement('script');
        const timeout = window.setTimeout(() => {
          finish(false);
        }, 15000);
        let settled = false;

        function finish(loaded) {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeout);
          script.removeEventListener('load', onLoad);
          script.removeEventListener('error', onError);
          if (loaded && window.Wistia && typeof window.Wistia.embed === 'function') {
            resolve();
          } else {
            if (!existingScript) script.remove();
            reject(new Error('Wistia could not load.'));
          }
        }

        function onLoad() {
          finish(true);
        }
        function onError() {
          finish(false);
        }

        script.addEventListener('load', onLoad);
        script.addEventListener('error', onError);

        if (!existingScript) {
          script.src = 'https://fast.wistia.com/assets/external/E-v1.js';
          script.async = true;
          document.head.appendChild(script);
        }
      }).catch((error) => {
        wistiaScriptPromise = undefined;
        throw error;
      });

      return wistiaScriptPromise;
    }

    function clearGuideVideo() {
      videoRequest += 1;
      window.clearTimeout(videoLoadTimeout);
      // Wistia's remove() stops streaming and cancels pending player initialization.
      if (activeVideo) activeVideo.remove();
      activeVideo = undefined;
      if (videoMount) videoMount.replaceChildren();
    }

    function loadGuideVideo(slot) {
      if (!slot || !videoFrame) return;

      clearGuideVideo();
      activeVideoSlot = slot;
      const request = videoRequest;
      const mediaId = slot.dataset.mediaId;

      if (videoFrame.parentElement !== slot) slot.appendChild(videoFrame);
      videoFrame.hidden = false;
      videoStatus.hidden = false;
      videoRetry.hidden = true;
      videoMount.setAttribute('aria-busy', mediaId ? 'true' : 'false');
      videoMessage.textContent = mediaId ? 'Loading brew guide…' : 'Brew guide video coming soon.';
      if (!mediaId) return;

      function showVideoError() {
        if (request !== videoRequest || !modalIsOpen) return;
        clearGuideVideo();
        videoMount.setAttribute('aria-busy', 'false');
        videoStatus.hidden = false;
        videoMessage.textContent = 'Unable to load this video. Please try again.';
        videoRetry.hidden = false;
      }

      loadWistia()
        .then(() => {
          // A close or tab change during the script request must not create a stale player.
          if (request !== videoRequest || !modalIsOpen) return;

          const embed = document.createElement('div');
          embed.id = 'wistia_' + mediaId;
          embed.className = 'wistia_embed';
          videoMount.appendChild(embed);

          // Only this active media is instantiated; no hidden embeds or per-video scripts.
          activeVideo = window.Wistia.embed(mediaId, {
            autoPlay: false,
            preload: 'none',
            videoFoam: true,
            fitStrategy: 'cover',
            playerColor: getComputedStyle(modal)
              .getPropertyValue('--advent-guide-gold')
              .trim()
              .replace('#', ''),
            // Animated thumbnails can stream a second video even with preload disabled.
            plugin: { videoThumbnail: { on: false } },
            thumbnailAltText: slot.dataset.videoTitle
          });
          videoLoadTimeout = window.setTimeout(showVideoError, 20000);
          activeVideo.ready(() => {
            if (request !== videoRequest || !modalIsOpen) return;
            window.clearTimeout(videoLoadTimeout);
            videoMount.setAttribute('aria-busy', 'false');
            videoStatus.hidden = true;
          });
        })
        .catch(showVideoError);
    }

    function showGuideTab(item, tabName) {
      const buttons = Array.from(item.querySelectorAll('[data-advent-tab]'));
      const activeButton = buttons.find((button) => button.dataset.adventTab === tabName);
      const panel = item.querySelector('[data-advent-panel]');
      const subtitles = Array.from(item.querySelectorAll('[data-advent-subtitle]'));

      if (!activeButton || !panel) return;

      if (tabName === 'batch' && !panel.querySelector('[data-advent-recipe="batch"]')) {
        panel.appendChild(batchTemplate.content.cloneNode(true));
      }

      buttons.forEach((button) => {
        const isActive = button === activeButton;

        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        button.tabIndex = isActive ? 0 : -1;
      });

      panel.setAttribute('aria-labelledby', activeButton.id);
      panel.querySelector('[data-advent-origin]').hidden = tabName === 'overview';

      panel.querySelectorAll('[data-advent-recipe]').forEach((recipe) => {
        recipe.hidden = recipe.dataset.adventRecipe !== tabName;
      });

      subtitles.forEach((subtitle) => {
        subtitle.hidden = subtitle.dataset.adventSubtitle !== tabName;
      });

      let slot;
      panel.querySelectorAll('[data-advent-video-slot]').forEach((videoSlot) => {
        const isActive = videoSlot.dataset.adventVideoSlot === tabName;
        videoSlot.hidden = !isActive;
        if (isActive) slot = videoSlot;
      });
      if (slot !== activeVideoSlot) loadGuideVideo(slot);
    }

    function openGuide(index) {
      if (!modal || !modalOverlay || !modalCloseButton || !canOpen(index)) return;

      const template = guideTemplates.get(index);
      if (!template || !guideContent) return;
      if (modalIsOpen && openGuideIndex === index) return;

      window.clearTimeout(modalCloseTimeout);
      if (!modalIsOpen) modalReturnFocus = document.activeElement;
      clearGuideVideo();
      activeVideoSlot = undefined;
      // Keep the single video frame (and its close button) when replacing a guide.
      modalDialog.prepend(videoFrame);
      guideContent.replaceChildren(template.content.cloneNode(true));
      const item = guideContent.firstElementChild;
      openGuideIndex = index;
      modalIsOpen = true;

      modal.setAttribute('aria-labelledby', 'advent-guide-title-' + (index + 1));
      modal.removeAttribute('aria-label');
      modal.hidden = false;
      modalOverlay.hidden = false;
      modal.scrollTop = 0;
      showGuideTab(item, 'featured');
      root.setAttribute('aria-hidden', 'true');
      root.inert = true;
      onChange();
      document.body.classList.add('advent-guide-is-open');

      window.requestAnimationFrame(() => {
        if (!modalIsOpen) return;
        modal.classList.add('is-active');
        modalOverlay.classList.add('is-active');
        modalCloseButton.focus({ preventScroll: true });
      });
    }

    function closeGuide() {
      if (!modal || !modalOverlay || !modalIsOpen) return;

      modalIsOpen = false;
      openGuideIndex = undefined;
      modal.classList.remove('is-active');
      modalOverlay.classList.remove('is-active');
      root.removeAttribute('aria-hidden');
      root.inert = false;
      onChange();
      document.body.classList.remove('advent-guide-is-open');
      clearGuideVideo();
      activeVideoSlot = undefined;

      modalCloseTimeout = window.setTimeout(
        () => {
          modal.hidden = true;
          modalOverlay.hidden = true;
          videoFrame.hidden = true;
          modalDialog.prepend(videoFrame);
          guideContent.replaceChildren();
        },
        reducedMotion ? 0 : 300
      );

      if (modalReturnFocus && typeof modalReturnFocus.focus === 'function')
        modalReturnFocus.focus({ preventScroll: true });
    }

    if (modal && modalOverlay && modalCloseButton) {
      modal.addEventListener('click', (event) => {
        const tabButton = event.target.closest('[data-advent-tab]');

        if (tabButton) {
          const item = guideContent.firstElementChild;
          if (item) showGuideTab(item, tabButton.dataset.adventTab);
        }

        if (event.target === modal || event.target === modalDialog) closeGuide();
      });

      modal.addEventListener('keydown', (event) => {
        const tabButton = event.target.closest('[data-advent-tab]');
        if (!tabButton) return;

        const buttons = Array.from(guideContent.querySelectorAll('[data-advent-tab]'));
        const index = buttons.indexOf(tabButton);
        let nextIndex;

        if (event.key === 'ArrowRight') nextIndex = (index + 1) % buttons.length;
        else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + buttons.length) % buttons.length;
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = buttons.length - 1;
        else return;

        event.preventDefault();
        // Move focus without requesting another video until Enter, Space, or a click.
        buttons.forEach((button, buttonIndex) => {
          button.tabIndex = buttonIndex === nextIndex ? 0 : -1;
        });
        buttons[nextIndex].focus({ preventScroll: true });
      });

      modalCloseButton.addEventListener('click', closeGuide);
      modalOverlay.addEventListener('click', closeGuide);
      videoRetry.addEventListener('click', () => {
        loadGuideVideo(activeVideoSlot);
      });

      document.addEventListener('keydown', (event) => {
        if (!modalIsOpen) return;

        if (event.key === 'Escape' && !document.fullscreenElement) {
          event.preventDefault();
          closeGuide();
          return;
        }

        if (event.key !== 'Tab') return;

        const focusable = Array.from(
          modal.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        ).filter(
          (element) =>
            element.tabIndex >= 0 && !element.closest('[hidden]') && element.getClientRects().length
        );

        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (
          event.shiftKey &&
          (document.activeElement === first || !modal.contains(document.activeElement))
        ) {
          event.preventDefault();
          last.focus();
        } else if (
          !event.shiftKey &&
          (document.activeElement === last || !modal.contains(document.activeElement))
        ) {
          event.preventDefault();
          first.focus();
        }
      });
    }

    return {
      open: openGuide,
      close: closeGuide,
      get isOpen() {
        return modalIsOpen;
      },
      get index() {
        return openGuideIndex;
      }
    };
  }

  function createExperienceController(root, reducedMotion, sprites, calendar, guide) {
    const intro = root.querySelector('[data-advent-intro]');
    const title = root.querySelector('.advent-title');
    const interactiveButton = root.querySelector('[data-advent-experience-interactive]');
    const revealButton = root.querySelector('[data-advent-reveal-open]');
    const actor = root.querySelector('[data-advent-experience-character]');
    const spotlight = root.querySelector('[data-advent-experience-spotlight]');
    const sparkles = root.querySelector('[data-advent-experience-sparkles]');
    const poof = root.querySelector('[data-advent-experience-poof]');
    const character = root.querySelector('[data-advent-character]');
    const host = sprites.host;
    const sequence = createSequence();
    const curtains = createSequence();
    const sparkleReaction = [
      ['front', 180],
      ['frontLeftTwo', 450],
      ['frontRight', 450],
      ['front', 300],
      // The existing "forward" pose has closed eyes; "front" has open eyes.
      ['forward', 120],
      ['front', 180],
      ['forward', 120],
      ['front', 260]
    ];
    let state = intro && revealButton ? 'closed' : 'open';
    let experience = 'arriving';
    let ready = false;

    function syncAvailability() {
      if (!intro) return;
      const available = state === 'closed' && experience === 'choosing' && ready;
      intro.inert = !available;
      revealButton.disabled = !available;
      interactiveButton.tabIndex = available ? 0 : -1;
      if (available) interactiveButton.removeAttribute('aria-disabled');
      else interactiveButton.setAttribute('aria-disabled', 'true');
    }
    function setExperience(value) {
      experience = value;
      root.dataset.adventExperienceState = value;
      intro.dataset.adventIntroState = value;
      syncAvailability();
    }
    function setState(value) {
      state = value;
      root.dataset.adventRevealState = value;
      syncAvailability();
      calendar.syncAvailability();
    }
    function emit(name, detail) {
      root.dispatchEvent(new CustomEvent('advent:' + name, { bubbles: true, detail }));
    }
    function reset(nextExperience = 'choosing') {
      sequence.cancel();
      host.stop();
      sprites.sparkles.stop();
      sprites.poof.stop();
      actor.classList.remove(
        'is-walking-away',
        'is-behind-curtain',
        'is-hidden',
        'is-pending-entrance'
      );
      actor.style.removeProperty('transform');
      sparkles.hidden = true;
      poof.hidden = true;
      spotlight.classList.remove('is-active');
      host.show('front');
      intro.hidden = false;
      title.classList.toggle('is-pending-entrance', nextExperience === 'arriving');
      setExperience(nextExperience);
    }
    const position = ({ x, y }) => `translate3d(calc(-50% + ${x}px), -${y}px, 0)`;
    function path() {
      const y = Math.max(58, Math.min(72, root.clientHeight * 0.076));
      return {
        start: { x: -Math.max(62, Math.min(88, root.clientWidth * 0.065)), y },
        turn: { x: Math.max(52, Math.min(84, root.clientWidth * 0.055)), y },
        end: { x: 0, y: 0 }
      };
    }
    async function walk(from, to, frames, duration, signal) {
      await Promise.all([
        animate(
          actor,
          [{ transform: position(from) }, { transform: position(to) }],
          { duration, easing: 'linear' },
          signal
        ),
        host.play(frames, { duration, frameDuration: 75, signal })
      ]);
      actor.style.transform = position(to);
    }
    async function enterHost(signal) {
      const { start, turn, end } = path();
      actor.classList.add('is-behind-curtain');
      await walk(
        start,
        turn,
        ['frontRightOne', 'frontRight', 'frontRightThree', 'frontRight'],
        422,
        signal
      );
      actor.classList.remove('is-behind-curtain');
      await walk(
        turn,
        end,
        ['frontLeftOne', 'frontLeftTwo', 'frontLeftThree', 'frontLeftTwo'],
        318,
        signal
      );
    }
    function arrive({ returnFocus = false } = {}) {
      reset('arriving');
      sequence.run(async (signal) => {
        title.classList.remove('is-pending-entrance');
        if (!reducedMotion) {
          await Promise.all([
            animate(
              title,
              [{ opacity: 0 }, { opacity: 1 }],
              { duration: 600, easing: 'ease-out' },
              signal
            ),
            enterHost(signal)
          ]);
        }
        actor.style.removeProperty('transform');
        host.show('front');
        setExperience('choosing');
        if (returnFocus) revealButton.focus({ preventScroll: true });
      });
    }
    function open() {
      setState('opening');
      curtains.run(async (signal) => {
        await calendar.enter(signal);
        setExperience('complete');
        setState('open');
        intro.hidden = true;
        emit('curtains-opened');
        calendar.finishEntrance();
      });
    }
    async function reactToSparkles(signal) {
      for (const [pose, duration] of sparkleReaction) {
        signal.throwIfAborted();
        host.show(pose);
        await wait(duration, signal);
      }
    }
    function choose(kind) {
      if (state !== 'closed' || experience !== 'choosing' || !ready) return;
      setExperience(kind === 'interactive' ? 'interacting' : 'leaving');
      emit('experience-selected', { experience: kind });
      sequence.run(async (signal) => {
        if (kind === 'interactive') {
          if (!reducedMotion) {
            // Effects are fetched only when this branch is selected.
            await Promise.all([sprites.sparkles.load(), sprites.poof.load()]);
            signal.throwIfAborted();
            spotlight.classList.add('is-active');
            await wait(420, signal);
            sparkles.hidden = false;
            await Promise.all([
              sprites.sparkles.play(['0', '1', '2', '3', '4'], {
                duration: sparkleReaction.reduce((total, [, duration]) => total + duration, 0),
                frameDuration: 114,
                signal
              }),
              reactToSparkles(signal)
            ]);
            sparkles.hidden = true;
            actor.classList.add('is-hidden');
            poof.style.transform = 'translate3d(-50%, 0, 0) scale(0.92)';
            poof.hidden = false;
            await sprites.poof.play(['0', '1', '2', '3', '4'], { duration: 390, signal });
            poof.hidden = true;
            await wait(500, signal);
          }
          window.location.assign(interactiveButton.href);
          return;
        }
        if (!reducedMotion) {
          const { start, turn, end } = path();
          actor.classList.add('is-walking-away');
          await walk(
            end,
            turn,
            ['backRightOne', 'backRightTwo', 'backRightThree', 'backRightTwo'],
            318,
            signal
          );
          actor.classList.add('is-behind-curtain');
          await walk(
            turn,
            start,
            ['backLeftOne', 'backLeftTwo', 'backLeftThree', 'backLeftTwo'],
            422,
            signal
          );
        }
        actor.classList.add('is-hidden');
        setExperience('complete');
        open();
      });
    }
    function close() {
      if (!intro || state === 'closing') return;
      if (state === 'closed') {
        if (experience !== 'choosing' && experience !== 'arriving') arrive({ returnFocus: true });
        return;
      }
      // Capture the current entrance position before cancelling its animation.
      const transform = getComputedStyle(character).transform;
      sequence.cancel();
      curtains.cancel();
      calendar.reset();
      character.style.transform = transform;
      intro.hidden = false;
      setState('closing');
      title.classList.add('is-pending-entrance');
      guide.close();
      calendar.freeze();
      curtains.run(async (signal) => {
        if (!reducedMotion) await wait(1400, signal);
        character.style.removeProperty('transform');
        setState('closed');
        arrive({ returnFocus: true });
        emit('curtains-closed');
      });
    }
    function start() {
      calendar.syncAvailability();
      if (!intro || !revealButton) {
        calendar.finishEntrance();
        return;
      }
      setState('closed');
      setExperience('arriving');
      const assets = [
        host.load(),
        ...Array.from(root.querySelectorAll('.advent-curtains img'), (image) =>
          loadSheet(image.src)
        )
      ];
      Promise.all(assets).then(() => {
        ready = true;
        arrive();
      });
      interactiveButton.addEventListener('click', (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
          return;
        event.preventDefault();
        choose('interactive');
      });
      revealButton.addEventListener('click', () => choose('simplified'));
      root.querySelector('[data-advent-back]')?.addEventListener('click', close);
      root.addEventListener('advent:open-curtains', () => choose('simplified'));
      root.addEventListener('advent:close-curtains', close);
      window.addEventListener('pageshow', (event) => {
        if (event.persisted && state === 'closed' && experience !== 'choosing') reset();
      });
    }
    return {
      start,
      get isOpen() {
        return state === 'open';
      }
    };
  }

  function createCalendarController(root, sliderElement, reducedMotion, sprite, guide, isOpen) {
    const dayButtons = Array.from(root.querySelectorAll('[data-advent-day]'));
    const dayButtonLabels = dayButtons.map((button) => button.getAttribute('aria-label'));
    const slides = dayButtons.map((button) => button.closest('.swiper-slide'));
    const previousButton = root.querySelector('[data-advent-previous]');
    const nextButton = root.querySelector('[data-advent-next]');
    const stage = root.querySelector('.advent-stage');
    const character = root.querySelector('[data-advent-character]');
    const stepDuration = 360;
    const stepsPerSlide = 2;
    const jumpDuration = 280;
    const lockedPieceBumpDuration = 320;
    const action = createSequence();
    const parallax = createParallax(root, reducedMotion);
    let dragWalkingActive = false;
    let idleTimeout;
    let lockedBumpTimeout;
    let headShakeReturnDirection;
    let pendingJumpIndex;
    let pendingGuideIndex;
    let bumpedIndex;
    let dragReleasePosition;
    let dragSettleActive = false;
    let heldDirection = 0;
    let heldKey;
    let heldPointerId;
    let queuedDirection = 0;
    let navigationTimer;
    let arrowNavigation = false;
    let availabilityTimer;

    function parseLocalDate(value) {
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
      if (!match) return;

      const year = Number.parseInt(match[1], 10);
      const monthIndex = Number.parseInt(match[2], 10) - 1;
      const day = Number.parseInt(match[3], 10);
      const date = new Date(year, monthIndex, day);

      // The numeric constructor creates midnight in the visitor's local timezone.
      if (date.getFullYear() !== year || date.getMonth() !== monthIndex || date.getDate() !== day)
        return;

      return date;
    }

    function getLocalCalendarDay(date) {
      // Compare calendar dates via UTC ordinals so daylight-saving changes cannot
      // turn a local calendar day into a 23- or 25-hour offset.
      return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86400000;
    }

    function getRequestedDayIndex() {
      const day = Number(new URL(window.location.href).searchParams.get('day'));

      return Number.isInteger(day) && day >= 1 && day <= dayButtons.length ? day - 1 : undefined;
    }

    const adventStartDate = parseLocalDate(root.dataset.adventStartDate);
    const requestedDayIndex = getRequestedDayIndex();

    function getCalendarUnlockedIndex(now) {
      if (!adventStartDate) return -1;

      return Math.max(
        -1,
        Math.min(
          dayButtons.length - 1,
          getLocalCalendarDay(now) - getLocalCalendarDay(adventStartDate)
        )
      );
    }

    function isDayUnlocked(index) {
      return Number.isInteger(index) && index >= 0 && index <= highestUnlockedIndex;
    }

    let highestUnlockedIndex = getCalendarUnlockedIndex(new Date());
    const initialSlideIndex =
      requestedDayIndex === undefined ? Math.max(0, highestUnlockedIndex) : requestedDayIndex;
    let pendingRequestedGuide = requestedDayIndex;

    if (!adventStartDate) {
      console.error('Advent brew guides: data-advent-start-date must use YYYY-MM-DD.');
    }

    function setPose(pose) {
      sprite.show(pose);
    }

    function clearLockedBump() {
      window.clearTimeout(lockedBumpTimeout);
      slides.forEach((slide) => slide.classList.remove('is-locked-bumping'));
    }

    function stopWalking() {
      action.cancel();
      sprite.stop();
      dragWalkingActive = false;
      if (headShakeReturnDirection !== undefined) {
        character.dataset.direction = headShakeReturnDirection;
        headShakeReturnDirection = undefined;
      }
      clearLockedBump();
      character.classList.remove('is-walking', 'is-jumping');
      setPose('stand');
    }

    function startWalking(direction, stepCount, duration, repeat = arrowNavigation) {
      window.clearTimeout(idleTimeout);
      stopWalking();
      character.dataset.direction = direction;
      const totalSteps = Math.max(1, stepCount);
      const durationPerStep = duration / totalSteps;
      character.style.setProperty('--advent-walk-step-duration', durationPerStep + 'ms');
      character.style.setProperty('--advent-walk-step-count', repeat ? 'infinite' : totalSteps);
      character.classList.add('is-walking');
      if (reducedMotion) return;
      sprite
        .play(['walkLeft', 'walkRight'], {
          duration: repeat ? durationPerStep * 2 : duration,
          frameDuration: durationPerStep,
          iterations: repeat ? Infinity : 1
        })
        .catch(ignoreCancellation);
    }

    function startDragWalking(direction) {
      startWalking(direction, stepsPerSlide, stepDuration, true);
      dragWalkingActive = true;
    }

    function bumpLockedPiece(index) {
      clearLockedBump();
      slides[index]?.classList.add('is-locked-bumping');
      lockedBumpTimeout = window.setTimeout(clearLockedBump, lockedPieceBumpDuration);
    }

    function jump(index, openGuideAfterJump) {
      const isLocked = !isDayUnlocked(index);
      stopWalking();
      window.clearTimeout(idleTimeout);
      setBumpedPiece();
      if (reducedMotion) {
        if (!isLocked) setBumpedPiece(index);
        if (openGuideAfterJump) guide.open(index);
        return;
      }
      action.run(async (signal) => {
        const bumpStart = Math.round(jumpDuration * 0.42);
        void character.offsetWidth;
        setPose('jump');
        character.classList.add('is-jumping');
        await wait(bumpStart, signal);
        if (isLocked) bumpLockedPiece(index);
        else setBumpedPiece(index);
        await wait(jumpDuration - bumpStart, signal);
        character.classList.remove('is-jumping');
        if (isLocked) {
          headShakeReturnDirection = character.dataset.direction;
          character.dataset.direction = 'right';
          setPose('forward');
          await wait(Math.max(0, bumpStart + lockedPieceBumpDuration - jumpDuration), signal);
          await sprite.play(
            [
              'headShakeRight',
              'forward',
              'headShakeLeft',
              'forward',
              'headShakeRight',
              'forward',
              'headShakeLeft',
              'forward'
            ],
            { duration: 880, signal }
          );
          character.dataset.direction = headShakeReturnDirection;
          headShakeReturnDirection = undefined;
        }
        setPose('stand');
        if (openGuideAfterJump) guide.open(index);
      });
    }

    function syncGameAvailability() {
      const available = isOpen() && !guide.isOpen;
      stage.inert = !available;
      if (available) {
        swiper.enable();
        swiper.mousewheel.enable();
      } else {
        cancelArrowNavigation();
        swiper.disable();
        swiper.mousewheel.disable();
      }
    }

    function reset() {
      window.clearTimeout(idleTimeout);
      pendingJumpIndex = undefined;
      pendingGuideIndex = undefined;
      stopWalking();
      setBumpedPiece();
    }

    async function enter(signal) {
      reset();
      character.style.removeProperty('transform');
      await sprite.load();
      signal.throwIfAborted();
      if (reducedMotion) return;
      const delay = 350;
      const duration = Math.round(Math.max(1200, Math.min(2400, root.clientWidth * 1.4)));
      await Promise.all([
        animate(
          character,
          [
            { transform: 'translate3d(calc(-50% - 50vw - var(--advent-character-width)), 0, 0)' },
            { transform: 'translate3d(-50%, 0, 0)' }
          ],
          { duration, delay, easing: 'linear' },
          signal
        ),
        wait(delay, signal).then(() => startWalking('right', Math.ceil(duration / 180), duration))
      ]);
      stopWalking();
    }

    function finishEntrance() {
      character.focus({ preventScroll: true });
      if (pendingRequestedGuide !== undefined) {
        const index = pendingRequestedGuide;
        pendingRequestedGuide = undefined;
        guide.open(index);
      }
    }
    function setBumpedPiece(index) {
      bumpedIndex = Number.isInteger(index) ? index : undefined;

      slides.forEach((slide, slideIndex) => {
        const hasBumpedPiece = bumpedIndex !== undefined;

        slide.classList.toggle('is-before-bumped', hasBumpedPiece && slideIndex < bumpedIndex);
        slide.classList.toggle('is-after-bumped', hasBumpedPiece && slideIndex > bumpedIndex);
        slide.classList.toggle('is-bumped', slideIndex === bumpedIndex);
      });
    }

    function syncSelectedDay(swiper) {
      dayButtons.forEach((button, index) => {
        const isActive = index === swiper.activeIndex;

        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      if (bumpedIndex !== undefined && bumpedIndex !== swiper.activeIndex) setBumpedPiece();

      const activeDayIsAvailable = isDayUnlocked(swiper.activeIndex);

      previousButton.disabled = swiper.activeIndex === 0;
      nextButton.disabled = swiper.activeIndex === dayButtons.length - 1;
      swiper.allowSlidePrev = swiper.activeIndex > 0;
      swiper.allowSlideNext = swiper.activeIndex < dayButtons.length - 1;
      character.disabled = false;
      character.setAttribute(
        'aria-label',
        activeDayIsAvailable
          ? 'Open brew guide for day ' + (swiper.activeIndex + 1)
          : 'Brew guide for day ' + (swiper.activeIndex + 1) + ' is not available yet'
      );
    }

    function getTrackPosition(swiper) {
      return swiper.rtlTranslate ? swiper.translate : -swiper.translate;
    }

    function isSliderInViewport() {
      const bounds = sliderElement.getBoundingClientRect();

      return (
        bounds.bottom > 0 &&
        bounds.right > 0 &&
        bounds.top < window.innerHeight &&
        bounds.left < window.innerWidth
      );
    }

    function releaseNavigationHold() {
      heldDirection = 0;
      heldKey = undefined;
      heldPointerId = undefined;
      window.clearTimeout(navigationTimer);
    }

    function cancelArrowNavigation() {
      releaseNavigationHold();
      queuedDirection = 0;
      arrowNavigation = false;
    }

    function navigateOneDay(direction) {
      if (guide.isOpen || !isOpen()) return false;
      if (swiper.animating) {
        // Keep at most one discrete press; key repeat never adds a backlog.
        queuedDirection = direction;
        return false;
      }

      queuedDirection = 0;
      const index = Math.max(0, Math.min(dayButtons.length - 1, swiper.activeIndex + direction));
      if (index === swiper.activeIndex) return false;

      action.cancel();
      pendingJumpIndex = undefined;
      pendingGuideIndex = undefined;
      arrowNavigation = true;
      swiper.slideTo(index, reducedMotion ? 0 : stepDuration);
      return true;
    }

    function continueArrowNavigation() {
      const direction = queuedDirection || heldDirection;
      if (!direction || guide.isOpen || !isOpen()) return false;

      if (reducedMotion) {
        // Instant slides still advance at the normal cadence while an arrow is held.
        window.clearTimeout(navigationTimer);
        navigationTimer = window.setTimeout(() => {
          navigateOneDay(queuedDirection || heldDirection);
        }, stepDuration);
        return false;
      }

      return navigateOneDay(direction);
    }

    function startDragSettle(swiper) {
      if (dragReleasePosition === undefined) return;

      const releasePosition = dragReleasePosition;
      const targetPosition = swiper.snapGrid[swiper.activeIndex] ?? releasePosition;
      const slideSize = swiper.slidesSizesGrid[swiper.activeIndex] || 1;
      const remainingDistance = Math.abs(targetPosition - releasePosition);
      const centerTolerance = Math.max(3, slideSize * 0.04);

      dragReleasePosition = undefined;
      dragSettleActive = true;

      if (reducedMotion || remainingDistance <= centerTolerance) {
        swiper.setTransition(reducedMotion ? 0 : 80);
        stopWalking();
        return;
      }

      const direction = targetPosition > releasePosition ? 'right' : 'left';
      const duration = Math.round(
        Math.max(160, Math.min(stepDuration, (stepDuration * remainingDistance) / slideSize))
      );

      swiper.setTransition(duration);
      startWalking(direction, stepsPerSlide, duration);
    }

    const swiper = new Swiper(sliderElement, {
      centeredSlides: true,
      focusableElements: 'input, select, option, textarea, video, label',
      grabCursor: true,
      mousewheel: {
        releaseOnEdges: true,
        thresholdDelta: 8
      },
      initialSlide: initialSlideIndex,
      preventInteractionOnTransition: true,
      threshold: 4,
      slidesPerView: 'auto',
      spaceBetween: 0,
      speed: reducedMotion ? 0 : stepDuration,
      on: {
        init: (instance) => {
          setBumpedPiece();
          syncSelectedDay(instance);
          parallax.update(instance);
        },
        resize: (instance) => {
          parallax.update(instance, true);
        },
        setTranslate: (instance) => {
          parallax.update(instance);
        },
        touchStart: () => {
          cancelArrowNavigation();
          dragReleasePosition = undefined;
          dragSettleActive = false;
        },
        sliderFirstMove: () => {
          action.cancel();
          window.clearTimeout(idleTimeout);
          pendingJumpIndex = undefined;
          pendingGuideIndex = undefined;
          sliderElement.classList.add('is-dragging');
          character.classList.remove('is-jumping');
          stopWalking();
          setBumpedPiece();
        },
        sliderMove: (instance) => {
          const direction = instance.touchesDirection === 'next' ? 'right' : 'left';

          if (dragWalkingActive) {
            character.dataset.direction = direction;
          } else {
            startDragWalking(direction);
          }
        },
        touchEnd: (instance) => {
          const moved = instance.touchEventsData && instance.touchEventsData.isMoved;

          sliderElement.classList.remove('is-dragging');
          if (moved) dragReleasePosition = getTrackPosition(instance);

          idleTimeout = window.setTimeout(() => {
            if (!instance.animating) {
              dragReleasePosition = undefined;
              dragSettleActive = false;
              stopWalking();
            }
          }, 80);
        },
        slideChange: (instance) => {
          syncSelectedDay(instance);
        },
        transitionStart: (instance) => {
          startDragSettle(instance);
        },
        transitionEnd: (instance) => {
          parallax.update(instance);
          if (dragWalkingActive) {
            stopWalking();
            return;
          }
          if (!dragSettleActive) return;

          dragSettleActive = false;
          stopWalking();
        },
        slideChangeTransitionStart: (instance) => {
          if (dragSettleActive || !isOpen()) return;

          const direction = instance.activeIndex > instance.previousIndex ? 'right' : 'left';
          const slideCount = Math.max(1, Math.abs(instance.activeIndex - instance.previousIndex));
          const duration = reducedMotion ? 0 : slideCount * stepDuration;

          instance.setTransition(duration);
          startWalking(direction, slideCount * stepsPerSlide, duration);
        },
        slideChangeTransitionEnd: (instance) => {
          // Start the next fixed-duration step in the same event, without an idle pose.
          if (arrowNavigation && continueArrowNavigation()) return;
          arrowNavigation = false;
          stopWalking();

          if (pendingJumpIndex === instance.activeIndex) {
            const shouldOpenGuide = pendingGuideIndex === instance.activeIndex;

            pendingJumpIndex = undefined;
            pendingGuideIndex = undefined;
            jump(instance.activeIndex, shouldOpenGuide);
          }
        }
      }
    });

    function activateDay(index, openGuideAfterJump) {
      if (swiper.animating || guide.isOpen || !isOpen()) return;
      cancelArrowNavigation();

      const canOpenGuide = openGuideAfterJump && isDayUnlocked(index);

      if (index === swiper.activeIndex) {
        pendingJumpIndex = undefined;
        pendingGuideIndex = undefined;
        jump(index, canOpenGuide);
        return;
      }

      pendingJumpIndex = index;
      pendingGuideIndex = canOpenGuide ? index : undefined;
      swiper.slideTo(
        index,
        reducedMotion ? 0 : Math.abs(index - swiper.activeIndex) * stepDuration
      );
    }

    dayButtons.forEach((button, index) => {
      button.addEventListener('click', () => {
        if (!swiper.allowClick) return;
        activateDay(index, true);
      });
    });

    character.addEventListener('click', () => {
      activateDay(swiper.activeIndex, true);
    });

    document.addEventListener('keydown', (event) => {
      const direction = ['ArrowRight', 'PageDown'].includes(event.key)
        ? 1
        : ['ArrowLeft', 'PageUp'].includes(event.key)
          ? -1
          : 0;
      const editing =
        event.target &&
        (event.target.isContentEditable ||
          (event.target.closest && event.target.closest('input, select, textarea')));

      if (
        direction &&
        !event.defaultPrevented &&
        !event.altKey &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.shiftKey &&
        !editing &&
        isOpen() &&
        !guide.isOpen &&
        isSliderInViewport()
      ) {
        event.preventDefault();
        if (event.repeat || heldKey === event.key) return;
        releaseNavigationHold();
        heldKey = event.key;
        heldDirection = direction;
        navigateOneDay(direction);
        return;
      }

      const pressedSpace = event.key === ' ' || event.code === 'Space';
      const interactiveTarget =
        event.target &&
        (event.target.isContentEditable ||
          (event.target.closest && event.target.closest('a, button, input, select, textarea')));

      if (
        event.defaultPrevented ||
        event.repeat ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        !pressedSpace ||
        !isOpen() ||
        interactiveTarget ||
        !isSliderInViewport()
      )
        return;

      event.preventDefault();
      activateDay(swiper.activeIndex, true);
    });

    document.addEventListener('keyup', (event) => {
      if (event.key === heldKey) releaseNavigationHold();
    });

    [
      [previousButton, -1],
      [nextButton, 1]
    ].forEach(([button, direction]) => {
      let pointerClick = false;

      button.addEventListener('pointerdown', (event) => {
        if (event.button !== 0 || button.disabled || guide.isOpen || !isOpen()) return;
        event.preventDefault();
        button.focus({ preventScroll: true });
        releaseNavigationHold();
        heldPointerId = event.pointerId;
        heldDirection = direction;
        pointerClick = true;
        navigateOneDay(direction);
      });

      button.addEventListener('click', (event) => {
        if (pointerClick && event.detail !== 0) {
          pointerClick = false;
          return;
        }
        pointerClick = false;
        navigateOneDay(direction);
      });
    });

    document.addEventListener('pointerup', (event) => {
      if (event.pointerId === heldPointerId) releaseNavigationHold();
    });
    document.addEventListener('pointercancel', (event) => {
      if (event.pointerId === heldPointerId) cancelArrowNavigation();
    });
    window.addEventListener('blur', cancelArrowNavigation);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        cancelArrowNavigation();
        return;
      }

      refreshDayAvailability();
      scheduleAvailabilityRefresh();
    });
    window.addEventListener('focus', () => {
      refreshDayAvailability();
      scheduleAvailabilityRefresh();
    });

    function refreshDayAvailability() {
      highestUnlockedIndex = getCalendarUnlockedIndex(new Date());

      if (guide.isOpen && !isDayUnlocked(guide.index)) guide.close();

      dayButtons.forEach((button, index) => {
        const available = isDayUnlocked(index);

        button.disabled = false;
        button.removeAttribute('aria-disabled');
        button.setAttribute(
          'aria-label',
          available ? dayButtonLabels[index] : 'Day ' + (index + 1) + ': brew guide locked'
        );
        slides[index].classList.toggle('is-locked', !available);
      });

      syncSelectedDay(swiper);
      syncGameAvailability();
    }

    function scheduleAvailabilityRefresh() {
      window.clearTimeout(availabilityTimer);

      const now = new Date();
      const nextLocalMidnight = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() + 1,
        0,
        0,
        0,
        100
      );

      availabilityTimer = window.setTimeout(() => {
        refreshDayAvailability();
        scheduleAvailabilityRefresh();
      }, nextLocalMidnight - now);
    }

    refreshDayAvailability();
    scheduleAvailabilityRefresh();
    return {
      isDayUnlocked,
      syncAvailability: syncGameAvailability,
      enter,
      finishEntrance,
      reset,
      freeze() {
        swiper.slideTo(swiper.activeIndex, 0, false);
      }
    };
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const sheets = JSON.parse(root.querySelector('[data-advent-sprites]').textContent);
  const sprite = (selector, name) =>
    createSprite(root.querySelector(selector + ' .advent-sprite'), sheets[name], reducedMotion);
  const sprites = {
    character: sprite('[data-advent-character]', 'character'),
    host: sprite('[data-advent-experience-character]', 'character'),
    poof: sprite('[data-advent-experience-poof]', 'poof'),
    sparkles: sprite('[data-advent-experience-sparkles]', 'sparkles')
  };
  let calendar;
  let experience;
  const guide = createGuideController(
    root,
    reducedMotion,
    (index) => experience.isOpen && calendar.isDayUnlocked(index),
    () => calendar.syncAvailability()
  );
  calendar = createCalendarController(
    root,
    sliderElement,
    reducedMotion,
    sprites.character,
    guide,
    () => experience?.isOpen ?? false
  );
  experience = createExperienceController(root, reducedMotion, sprites, calendar, guide);
  experience.start();
})();
